import { AxiosError } from 'axios';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  Container,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { ChangeEvent, FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '@thingslog/queries';
import {
  Device,
  DeviceFilterAttributes,
  ErrorResponse,
  LicenseDto,
  LicenseType,
  UnlinkDeviceResult,
} from '@thingslog/repositories';
import { LicenseWarningBox, useToast } from '@thingslog/ui-components';
import JwtValidator from '../../common/JwtValidator';
import GoogleAnalyticsService from '../../common/GoogleAnalyticsService';
import { GaEventCategory } from '../../common/GaEventCategory';
import { GaEventAction } from '../../common/GaEventAction';
import Header from '../../components/header';
import {
  devicesQueryClient,
  licenseQueryClient,
  linkDevicesQueryClient,
} from '../../clients/ReactQueryClients/ReactQueryClients';
import useDetermineCompanyId from '../../hooks/useDetermineCompanyId';
import _ from 'lodash';

const LinkDevicesV2: FC<LinkDevicesProps> = () => {
  const [linkDeviceNumber, setLinkDeviceNumber] = useState<string | null>(null);
  const [unlinkDevicesForSubAccounts, setUnlinkDevicesForSubAccounts] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [lookupInputValue, setLookupInputValue] = useState('');
  const [queryFilterValue, setQueryFilterValue] = useState<string | null>(null);
  const [queryAttributes, setQueryAttributes] = useState<DeviceFilterAttributes>(
    DeviceFilterAttributes.deviceNumber
  );

  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { hasRole } = useMemo(() => new JwtValidator(), []);
  const { useLinkDevice, useUnlinkDevice } = useMemo(() => linkDevicesQueryClient, []);
  const { useLicenses, useDeviceLimits } = useMemo(() => licenseQueryClient, []);
  const { useDevicesData } = useMemo(() => devicesQueryClient, []);

  const isSuperAdmin = hasRole('ROLE_SUPER_ADMIN');
  const companyId = useDetermineCompanyId();

  const devicesQuery = useDevicesData(null, page, size, queryAttributes, queryFilterValue, {
    onError: (error: AxiosError) => {
      toast({
        type: 'error',
        message: t('devices_table_error'),
        duration: 5000,
      });
    },
    enabled: isSuperAdmin,
  });

  const linkDeviceMutation = useLinkDevice({
    onMutate: () => {
      GoogleAnalyticsService.triggerEvent(
        GaEventCategory.LINK_DEVICE_PAGE,
        GaEventAction.LINK_DEVICE_PAGE_LINK_DEVICE
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.UseDeviceData]);
      queryClient.invalidateQueries([QueryKeys.GetDeviceLimits]);

      toast({
        type: 'success',
        message: t('link_device_success_message'),
        duration: 5000,
      });
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      const errorMessage = error.response?.data.message || t('link_device_default_error');

      toast({
        type: 'error',
        message: errorMessage,
        duration: 5000,
      });
    },
  });

  const unlinkDeviceMutation = useUnlinkDevice({
    onMutate: () => {
      GoogleAnalyticsService.triggerEvent(
        GaEventCategory.LINK_DEVICE_PAGE,
        GaEventAction.LINK_DEVICE_PAGE_UNLINK_DEVICE
      );
    },
    onSuccess: (result: UnlinkDeviceResult) => {
      queryClient.invalidateQueries([QueryKeys.UseDeviceData]);
      queryClient.invalidateQueries([QueryKeys.GetDeviceLimits]);
      const companies =
        result.updatedCompanies.length > 0 ? result.updatedCompanies.join(', ') : t('none');
      toast({
        type: 'success',
        message: t('unlink_device_success_message') + companies,
        duration: 5000,
      });
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      const errorMessage = error.response?.data.message || t('link_device_default_error');

      toast({
        type: 'error',
        message: errorMessage,
        duration: 5000,
      });
    },
  });

  const licensesQuery = useLicenses(companyId, undefined, undefined, {
    enabled: !!companyId,
  });

  const perDeviceLicenses = useMemo(() => {
    return (
      licensesQuery.data?.filter(
        (license: LicenseDto) => license['@type'] === LicenseType.PER_DEVICE
      ) ?? []
    );
  }, [licensesQuery.data]);

  const hasPerDeviceLicense = useMemo(() => {
    return perDeviceLicenses.length > 0 && licensesQuery.isFetched;
  }, [perDeviceLicenses, licensesQuery.isFetched]);

  const deviceLimitsQuery = useDeviceLimits(companyId!, {
    enabled: companyId !== null && hasPerDeviceLicense,
  });

  const hasReachedDeviceLimit = useMemo(() => {
    return deviceLimitsQuery.data?.hasReachedLimit ?? false;
  }, [deviceLimitsQuery.data]);

  const isLookupByName = queryAttributes === DeviceFilterAttributes.deviceName;

  const subheader = useMemo(() => {
    return deviceLimitsQuery.data?.['@type'] === 'DEVICE_LIMITS'
      ? t('link_device_devices_counts', {
          linked_devices_count: deviceLimitsQuery.data.linkedDevices,
          allowed_devices_count: deviceLimitsQuery.data.maxDataLoggers,
        })
      : undefined;
  }, [deviceLimitsQuery.data]);

  const debouncedSetQueryFilterValue = useMemo(
    () =>
      _.debounce((value: string): void => {
        setQueryFilterValue(value || null);
      }, 400),
    []
  );
  const lookupDevices = devicesQuery.data?.content ?? [];

  useEffect(() => {
    return () => {
      debouncedSetQueryFilterValue.cancel();
    };
  }, [debouncedSetQueryFilterValue]);

  const isLinkDeviceDisabled = useMemo(() => {
    return (
      linkDeviceMutation.isLoading ||
      licensesQuery.isFetching ||
      deviceLimitsQuery.isFetching ||
      hasReachedDeviceLimit
    );
  }, [
    linkDeviceMutation.isLoading,
    licensesQuery.isFetching,
    deviceLimitsQuery.isFetching,
    hasReachedDeviceLimit,
  ]);

  const handleDeviceNumberChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setLinkDeviceNumber(event.target.value);
  };

  const handleLinkDevice = useCallback(() => {
    linkDeviceMutation.mutate({
      companyId: companyId!,
      deviceNumber: linkDeviceNumber!,
    });
  }, [companyId, linkDeviceNumber, linkDeviceMutation]);

  const handleUnlinkDevice = useCallback(() => {
    unlinkDeviceMutation.mutate({
      companyId: companyId!,
      deviceNumber: linkDeviceNumber!,
      removeForSubAccounts: unlinkDevicesForSubAccounts,
    });
  }, [companyId, linkDeviceNumber, unlinkDevicesForSubAccounts, unlinkDeviceMutation]);

  const handleUnlinkDevicesForSubAccountsChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setUnlinkDevicesForSubAccounts(event.target.checked);
  };

  const handleLookupAttributesChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setPage(0);
    setQueryAttributes(
      event.target.checked ? DeviceFilterAttributes.deviceName : DeviceFilterAttributes.deviceNumber
    );
  };

  const handleLookupInputValueChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setPage(0);
    setLookupInputValue(event.target.value);
    debouncedSetQueryFilterValue(event.target.value);
  };

  const handlePageChange = (event: unknown, newPage: number): void => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    setSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeviceNumberClick = (device: Device): void => {
    setLinkDeviceNumber(device.number);
  };

  return (
    <Header>
      <Container className="pt-5">
        <div className="flex flex-col md:flex-row justify-center gap-5">
          <div className="w-full md:w-1/2">
            <Card>
              <CardHeader
                title={t('link_device_link_device_to_my_account')}
                subheader={subheader}
                subheaderTypographyProps={{ className: 'text-red-500' }}
              />
              <CardContent>
                <TextField
                  className="mb-4 bg-white"
                  fullWidth
                  size="small"
                  label={t('link_device_device_number')}
                  value={linkDeviceNumber ?? ''}
                  onChange={handleDeviceNumberChange}
                />
                {hasReachedDeviceLimit && (
                  <LicenseWarningBox
                    warningStatus={{
                      devicesCountLimitReached: true,
                      noActiveLicense: false,
                      noValidLicense: false,
                    }}
                    translation={{
                      accessOnHoldTitle: t('license_warning_access_hold'),
                      accessOnHoldDescription: t('license_warning_access_hold_description'),
                      noValidLicenseTitle: t('license_warning_no_valid_license'),
                      noValidLicenseDescription: t('license_warning_no_valid_license_description'),
                      deviceLimitReachedTitle: t('license_warning_devices_count_limit_reached'),
                      deviceLimitReachedDescription: t(
                        'license_warning_devices_count_limit_reached_description'
                      ),
                      defaultGenericTitle: t('license_warning_generic_title'),
                      defaultGenericDescription: t('license_warning_generic_description'),
                    }}
                  />
                )}
              </CardContent>
              <CardActions className="flex flex-wrap items-start gap-1">
                <div className="flex w-full gap-2 md:w-auto">
                  <Button disabled={isLinkDeviceDisabled} onClick={handleLinkDevice}>
                    {t('link_link_device')}
                  </Button>
                  <Button onClick={handleUnlinkDevice}>{t('link_device_unlink_device')}</Button>
                </div>
                <Tooltip title={t('link_device_unlink_devices_for_sub_accounts_tooltip') || ''}>
                  <FormControlLabel
                    className="ml-0 mt-1 my-0 w-full"
                    control={
                      <Checkbox
                        checked={unlinkDevicesForSubAccounts}
                        onChange={handleUnlinkDevicesForSubAccountsChange}
                        size="small"
                      />
                    }
                    label={t('link_device_unlink_devices_for_sub_accounts')}
                  />
                </Tooltip>
              </CardActions>
            </Card>
          </div>
          {isSuperAdmin && (
            <div className="w-full md:w-1/2">
              <Card>
                <CardHeader
                  title={t('link_device_lookup_title')}
                  subheader={t('link_device_lookup_subheader')}
                />
                <CardContent>
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-4 md:flex-row md:items-center">
                      <div className="flex items-center gap-3 whitespace-nowrap">
                        <Typography
                          variant="body2"
                          color={!isLookupByName ? 'text.primary' : 'text.secondary'}
                        >
                          {t('link_device_lookup_filter_number')}
                        </Typography>
                        <Switch
                          size="small"
                          checked={isLookupByName}
                          onChange={handleLookupAttributesChange}
                        />
                        <Typography
                          variant="body2"
                          color={isLookupByName ? 'text.primary' : 'text.secondary'}
                        >
                          {t('link_device_lookup_filter_name')}
                        </Typography>
                      </div>
                      <TextField
                        fullWidth
                        size="small"
                        label={
                          isLookupByName
                            ? t('link_device_lookup_device_name')
                            : t('link_device_lookup_device_number')
                        }
                        value={lookupInputValue}
                        onChange={handleLookupInputValueChange}
                      />
                    </div>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('link_device_lookup_device_name')}</TableCell>
                            <TableCell>{t('link_device_lookup_device_number')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {lookupDevices.map((device: Device) => (
                            <TableRow
                              hover
                              key={device.number}
                              selected={device.number === linkDeviceNumber}
                              onClick={(): void => handleDeviceNumberClick(device)}
                              className="cursor-pointer"
                            >
                              <TableCell>{device.name}</TableCell>
                              <TableCell>{device.number}</TableCell>
                            </TableRow>
                          ))}
                          {lookupDevices.length === 0 && !devicesQuery.isLoading && (
                            <TableRow>
                              <TableCell align="center" colSpan={2}>
                                {t('link_device_lookup_no_devices_found')}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      component="div"
                      count={devicesQuery.data?.totalElements ?? 0}
                      page={page}
                      onPageChange={handlePageChange}
                      rowsPerPage={size}
                      onRowsPerPageChange={handleRowsPerPageChange}
                      rowsPerPageOptions={[5, 10, 25, 50]}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </Container>
    </Header>
  );
};

interface LinkDevicesProps {}

export default LinkDevicesV2;
